import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Grid, Header, Message } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const OPTION_KEYS = [
  'WeChatPayAppId',
  'WeChatPayMchId',
  'WeChatPaySerialNo',
  'WeChatPayAPIv3Key',
  'WeChatPayPrivateKey',
  'WeChatPayPublicKeyId',
  'WeChatPayPublicKey',
  'WeChatPayNotifyUrl',
  'WeChatPayQuotaPerYuan',
  'WeChatPayEnabled',
];

const defaultInputs = {
  WeChatPayAppId: '',
  WeChatPayMchId: '',
  WeChatPaySerialNo: '',
  WeChatPayAPIv3Key: '',
  WeChatPayPrivateKey: '',
  WeChatPayPublicKeyId: '',
  WeChatPayPublicKey: '',
  WeChatPayNotifyUrl: '',
  WeChatPayQuotaPerYuan: '',
  WeChatPayEnabled: 'false',
};

const WeChatPaySetting = () => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState(defaultInputs);
  const [loading, setLoading] = useState(false);

  const getWeChatPayOptions = async () => {
    setLoading(true);
    const res = await API.get('/api/option/');
    if (!res) {
      setLoading(false);
      return;
    }
    const { success, message, data } = res.data;
    if (success) {
      const optionMap = data.reduce((options, item) => {
        options[item.key] = item.value;
        return options;
      }, {});
      const newInputs = OPTION_KEYS.reduce((inputs, key) => {
        inputs[key] = optionMap[key] ?? defaultInputs[key];
        return inputs;
      }, {});
      setInputs(newInputs);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    getWeChatPayOptions().then();
  }, []);

  const handleInputChange = (e, { name, value, checked }) => {
    setInputs((inputs) => ({
      ...inputs,
      [name]: name === 'WeChatPayEnabled' ? String(checked) : value,
    }));
  };

  const submitWeChatPay = async () => {
    setLoading(true);
    const res = await API.put('/api/option/batch', {
      options: inputs,
    });
    if (!res) {
      setLoading(false);
      return;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('setting.system.wechat_pay.messages.saved'));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  return (
    <Grid columns={1}>
      <Grid.Column>
        <Form loading={loading}>
          <Header as='h3'>{t('setting.system.wechat_pay.title')}</Header>
          <Message>{t('setting.system.wechat_pay.subtitle')}</Message>
          <Form.Group inline>
            <Form.Checkbox
              toggle
              checked={inputs.WeChatPayEnabled === 'true'}
              label={t('setting.system.wechat_pay.enabled')}
              name='WeChatPayEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths={3}>
            <Form.Input
              label={t('setting.system.wechat_pay.app_id')}
              placeholder='wx...'
              name='WeChatPayAppId'
              onChange={handleInputChange}
              value={inputs.WeChatPayAppId}
            />
            <Form.Input
              label={t('setting.system.wechat_pay.mch_id')}
              placeholder={t('setting.system.wechat_pay.mch_id_placeholder')}
              name='WeChatPayMchId'
              onChange={handleInputChange}
              value={inputs.WeChatPayMchId}
            />
            <Form.Input
              label={t('setting.system.wechat_pay.serial_no')}
              placeholder={t('setting.system.wechat_pay.serial_no_placeholder')}
              name='WeChatPaySerialNo'
              onChange={handleInputChange}
              value={inputs.WeChatPaySerialNo}
            />
          </Form.Group>
          <Form.Group widths={3}>
            <Form.Input
              label={t('setting.system.wechat_pay.api_v3_key')}
              placeholder={t(
                'setting.system.wechat_pay.api_v3_key_placeholder'
              )}
              name='WeChatPayAPIv3Key'
              onChange={handleInputChange}
              type='password'
              autoComplete='new-password'
              value={inputs.WeChatPayAPIv3Key}
            />
            <Form.Input
              label={t('setting.system.wechat_pay.notify_url')}
              placeholder='https://yourdomain.com/api/payment/wechat/notify'
              name='WeChatPayNotifyUrl'
              onChange={handleInputChange}
              value={inputs.WeChatPayNotifyUrl}
            />
            <Form.Input
              label={t('setting.system.wechat_pay.quota_per_yuan')}
              placeholder='500000'
              name='WeChatPayQuotaPerYuan'
              onChange={handleInputChange}
              value={inputs.WeChatPayQuotaPerYuan}
            />
          </Form.Group>
          <Form.Group widths={3}>
            <Form.Input
              label={t('setting.system.wechat_pay.public_key_id')}
              placeholder={t(
                'setting.system.wechat_pay.public_key_id_placeholder'
              )}
              name='WeChatPayPublicKeyId'
              onChange={handleInputChange}
              value={inputs.WeChatPayPublicKeyId}
            />
          </Form.Group>
          <Form.TextArea
            label={t('setting.system.wechat_pay.private_key')}
            placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
            name='WeChatPayPrivateKey'
            onChange={handleInputChange}
            value={inputs.WeChatPayPrivateKey}
            rows={8}
          />
          <Form.TextArea
            label={t('setting.system.wechat_pay.public_key')}
            placeholder={'-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----'}
            name='WeChatPayPublicKey'
            onChange={handleInputChange}
            value={inputs.WeChatPayPublicKey}
            rows={8}
          />
          <Form.Button onClick={submitWeChatPay}>
            {t('setting.system.wechat_pay.buttons.save')}
          </Form.Button>
        </Form>
      </Grid.Column>
    </Grid>
  );
};

export default WeChatPaySetting;
